/**
 * Shared tax calculation utilities.
 *
 * All rounding happens at the LINE level (unit_price * quantity), never per unit.
 * This matches standard accounting practice and avoids per-unit rounding errors
 * that accumulate across quantities.
 */

/**
 * Tax treatment values stored in the database. `non_reclaimable` ("VAT not
 * reclaimable") is a VAT purchase-line treatment only (expenses, AP bills —
 * the DB CHECK refuses it on AR): VAT was charged but cannot be deducted, so
 * the line is stored at rate 0 with no `tax_rate_id` and its gross amount is
 * booked as cost with no VAT leg (docs/TAX.md "VAT zero-rate options").
 */
export type TaxTreatment = 'standard' | 'exempt' | 'reverse_charge' | 'zero_rated' | 'non_reclaimable';

/** Form-level tax rate type (number or special string). Canonical definition used across the app. */
export type TaxRateType = number | 'exempt' | 'non_reclaimable';

/**
 * A sales (AR / channel sale) line's form value: never 'non_reclaimable' —
 * input-VAT deduction is a purchase concept, and the DB CHECK refuses it on
 * AR lines.
 */
export type SalesTaxRateType = Exclude<TaxRateType, 'non_reclaimable'>;

/**
 * Convert a form-level TaxRateType + reverse charge flag to DB-ready fields.
 *
 * @param taxRate          - The form value (number, 'exempt' or 'non_reclaimable')
 * @param isReverseCharge  - Whether reverse charge applies to this line
 * @returns Object with numeric tax_rate and tax_treatment string for DB storage
 */
export function resolveTaxTreatment(
  taxRate: SalesTaxRateType,
  isReverseCharge?: boolean,
): { tax_rate: number; tax_treatment: Exclude<TaxTreatment, 'non_reclaimable'> };
export function resolveTaxTreatment(
  taxRate: TaxRateType,
  isReverseCharge?: boolean,
): { tax_rate: number; tax_treatment: TaxTreatment };
export function resolveTaxTreatment(taxRate: TaxRateType, isReverseCharge = false): { tax_rate: number; tax_treatment: TaxTreatment } {
  if (isReverseCharge) {
    const numericRate = typeof taxRate === 'number' ? taxRate : 0;
    return { tax_rate: numericRate, tax_treatment: 'reverse_charge' };
  }
  if (taxRate === 'exempt') {
    return { tax_rate: 0, tax_treatment: 'exempt' };
  }
  if (taxRate === 'non_reclaimable') {
    return { tax_rate: 0, tax_treatment: 'non_reclaimable' };
  }
  if (taxRate === 0) {
    return { tax_rate: 0, tax_treatment: 'zero_rated' };
  }
  return { tax_rate: taxRate, tax_treatment: 'standard' };
}

/**
 * Convert DB-stored tax_treatment + tax_rate back to form-level fields.
 *
 * @param taxTreatment - The DB tax_treatment value
 * @param taxRate      - The DB numeric tax_rate
 * @returns Object with form-level taxRate and isReverseCharge flag
 */
export function toTaxRateType(taxTreatment: string | null | undefined, taxRate: number): { taxRate: TaxRateType; isReverseCharge: boolean } {
  switch (taxTreatment) {
    case 'reverse_charge':
      return { taxRate: taxRate, isReverseCharge: true };
    case 'exempt':
      return { taxRate: 'exempt', isReverseCharge: false };
    // Must round-trip: hydrated as a plain 0 it would re-save as zero_rated
    // on the next edit, clone or recurring render, silently losing the fact.
    case 'non_reclaimable':
      return { taxRate: 'non_reclaimable', isReverseCharge: false };
    default:
      return { taxRate: taxRate, isReverseCharge: false };
  }
}

/**
 * `toTaxRateType` for a sales line: the same, typed without
 * 'non_reclaimable' (which the DB refuses on AR, so a stored sales line never
 * carries it — read defensively as a plain 0 if one ever did).
 */
export function toSalesTaxRateType(taxTreatment: string | null | undefined, taxRate: number): { taxRate: SalesTaxRateType; isReverseCharge: boolean } {
  const resolved = toTaxRateType(taxTreatment, taxRate);
  return resolved.taxRate === 'non_reclaimable'
    ? { taxRate: 0, isReverseCharge: resolved.isReverseCharge }
    : { taxRate: resolved.taxRate, isReverseCharge: resolved.isReverseCharge };
}

/** Round a number to 2 decimal places (currency precision). */
function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Precision for stored unit prices (6 decimals) to avoid floating-point display artifacts. */
const UNIT_PRICE_PRECISION = 1_000_000;

/**
 * Calculate the rounded line subtotal (pre-tax).
 *
 * @param unitPrice - Price per unit (may have extended precision)
 * @param quantity  - Number of units
 * @returns Line subtotal rounded to 2 decimals
 */
export function calculateLineSubtotal(unitPrice: number, quantity: number): number {
  return roundCurrency((unitPrice || 0) * (quantity || 0));
}

/**
 * Calculate the rounded tax amount for a line item.
 *
 * Formula: round(tax_per_unit * quantity)
 * Tax per unit is NOT rounded first — only the line total is rounded.
 *
 * @param unitPrice - Pre-tax price per unit
 * @param taxRate   - Tax rate as a percentage (0–100)
 * @param quantity  - Number of units
 * @returns Tax amount rounded to 2 decimals
 */
export function calculateLineTax(unitPrice: number, taxRate: number, quantity: number): number {
  const taxPerUnit = (unitPrice || 0) * ((taxRate || 0) / 100);
  return roundCurrency(taxPerUnit * (quantity || 0));
}

/**
 * Calculate a line total including tax (subtotal + tax).
 *
 * Each component is rounded individually (EU VAT rule), then summed. The sum is
 * also passed through roundCurrency to clean up floating-point drift — e.g.
 * 10.03 + 2.11 evaluates to 12.139999999999999 in IEEE-754, which would surface
 * to any caller that compares the result against an expected 2-decimal value.
 *
 * @param unitPrice - Pre-tax price per unit
 * @param taxRate   - Tax rate as a percentage (0–100)
 * @param quantity  - Number of units
 * @returns Line total (subtotal + tax) rounded to 2 decimals
 */
export function calculateLineTotalWithTax(unitPrice: number, taxRate: number, quantity: number): number {
  return roundCurrency(calculateLineSubtotal(unitPrice, quantity) + calculateLineTax(unitPrice, taxRate, quantity));
}

/**
 * Convert a tax-inclusive unit price to a pre-tax unit price.
 *
 * The result is rounded to 6 decimal places (not 2) so that line-level
 * calculations (unit_price × quantity) round correctly at the line total.
 *
 * @param taxInclusiveUnitPrice - The unit price that includes tax
 * @param taxRate               - Tax rate as a percentage (0–100)
 * @returns Pre-tax unit price rounded to 6 decimals
 */
export function calculatePreTaxUnitPrice(taxInclusiveUnitPrice: number, taxRate: number): number {
  const divisor = 1 + ((taxRate || 0) / 100);
  if (divisor <= 0) return taxInclusiveUnitPrice;
  return Math.round((taxInclusiveUnitPrice / divisor) * UNIT_PRICE_PRECISION) / UNIT_PRICE_PRECISION;
}
